import { useMemo } from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import { notNull } from '~/_shared/utils/typeGuards';
import { ExportReportType } from '~/exportReport/exportReportType';
import { ExportReportPrintPageWrapperComponent } from '~/exportReport/printPage/exportReportPrintPageWrapper.component';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import type { ExportReportLocationParams } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';

type ExportReportPrintPageLocationProps = Readonly<{
  params: ExportReportLocationParams;
  template: ReportTemplate;
}>;

export const ExportReportPrintPageLocationComponent = ({ template, params }: ExportReportPrintPageLocationProps) => {
  const spreadsheetData = useLatLngSpreadsheetData();
  const rowData = spreadsheetData.getRow(params.data.markerId);
  const { tableColumns } = useExportReportParams();
  const { getDemographicData, getDemographicLabel } = useExportReportData({
    tableColumns,
  });

  const radiusInMetersList = useMemo(() => {
    return tableColumns.map(column => column.type === 'radius' ? convertDistanceToUnitSystem({
      distance: column.distance * 1000,
      currentUnitSystem: column.unit,
      targetUnitSystem: UnitSystem.metric,
    }) : null).filter(notNull);
  }, [tableColumns]);

  if (!rowData) {
    return null;
  }

  return (
    <ExportReportPrintPageWrapperComponent>
      <ExportReportTemplateRendererComponent
        template={template}
        summaryProps={{
          latLng: {
            lat: rowData.lat,
            lng: rowData.lng,
          },
          heading: params.data.summary.heading,
          semiHeading: params.data.summary.semiHeading,
          description: params.data.summary.description,
        }}
        mapProps={{
          type: ExportReportType.Location,
          center: rowData,
        }}
        roadmapProps={{
          radiusInMeters: radiusInMetersList,
        }}
        dataSourceColumns={tableColumns}
        getDemographicLabel={getDemographicLabel}
        getDemographicData={getDemographicData}
      />
    </ExportReportPrintPageWrapperComponent>
  );
};
